import { ENV } from "./types";
import version from "../version.json";

/**
 * Get the element from the DOM, if none is found, throw an error.
 *
 * @param selector string of the current element
 * @returns HTMLElement | throw exception
 */
export const getElement = (selector: string) => {
  const element = document.getElementById(selector.replace("#", ""));
  if (element) {
    return element;
  } else {
    elementNotFound();
  }
};

/**
 * Throws an error if the element is not found.
 */
export function elementNotFound() {
  throw new Error(
    "Element not found, please add an id selector for the iframe"
  );
}

export function getCheckoutUrlBasedOnEnv(env: ENV, customCheckoutUrl?: string) {
  if (customCheckoutUrl) {
    return customCheckoutUrl;
  }

  switch (env) {
    case "LOCAL":
      return "http://localhost:1350";
    case "TEST":
    case "SANDBOX":
      return "https://checkout.sandbox.getalma.eu";
    case "STAGING":
      return "https://checkout.staging.almapay.com";
    case "DEV":
      return "https://checkout.dev.almapay.com";
    case "LIVE":
    case "PROD":
    default:
      return "https://checkout.getalma.eu";
  }
}

export function getAlmaAPIUrlBasedOnEnv(env: ENV) {
  switch (env) {
    case "LOCAL":
      return "http://localhost:1337";
    case "TEST":
    case "SANDBOX":
      return "https://api.sandbox.getalma.eu";
    case "STAGING":
      return "https://api.staging.almapay.com";
    case "DEV":
      return "https://api.dev.almapay.com";
    case "LIVE":
    case "PROD":
    default:
      return "https://api.getalma.eu";
  }
}

/**
 * Always logged in the console. Outside production, also shown in an alert
 * so integration mistakes are hard to miss during development.
 */
export function reportIntegrationError(message: string, env: ENV) {
  const fullMessage = `[Alma InPage] ${message}`;
  console.error(fullMessage);
  if (env !== "PROD" && env !== "LIVE") {
    alert(fullMessage);
  }
  return fullMessage;
}

export function fetchReturnUrls(paymentId: string, env: ENV) {
  const url = getAlmaAPIUrlBasedOnEnv(env);

  return fetch(`${url}/v1/checkout/payments/${paymentId}/return-urls`, {
    headers: {
      "X-Alma-Agent": `Alma Checkout - InPage/${getVersion()}`,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.error_code) {
        let hint = "";
        if (data.error_code === "not_found") {
          hint = `\n\nPlease check that payment "${paymentId}" has been created on the ${env} environment (${url}).`;
        }
        reportIntegrationError(
          `Error: ${data.error_code}\n\n${data.message}${hint}`,
          env
        );
      } else if (data.origin && data.origin !== "online_in_page") {
        reportIntegrationError(
          'Your payment does not have the proper origin for an in-page payment, you should use "online_in_page". \n\nPlease, check the documentation:\nhttps://docs.almapay.com/docs/integration-in-page',
          env
        );
      }
      return { success: data.return_url, failure: data.failure_return_url };
    })
    .catch((error) => {
      console.error("[Alma InPage] Failed to fetch payment return URLs:", error);
      return { success: undefined, failure: undefined };
    });
}

export function getCheckoutUrl(
  paymentId: string,
  env: ENV,
  url: string,
  customCheckoutUrl?: string
) {
  return (
    getCheckoutUrlBasedOnEnv(env, customCheckoutUrl) + "/" + paymentId + url
  );
}

export function getVersion() {
  const match = version.match(/\d+\.\d+\.\d+/);
  if (match) {
    return match[0];
  }

  return "2.0.0";
}
